import React, { useState, useEffect } from "react";
import { fetchReview } from "../../services/reviewServices";
import { Icon } from "@iconify/react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import SuccessAlert from "../../components/child/Alert/SuccessAlert";
import DengerAlert from "../../components/child/Alert/DengerAlert";
import { showConfirmationDialog } from "../../utils/apiHelper";
import { formatApiDate } from "../../utils/dateHelper";

const ReviewListLayer = ({ userId }) => {
  const [items, setItems] = useState([]);
  const [sortColumn, setSortColumn] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [searchQuery, setSearchQuery] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  // ✅ initialize from URL
  const [typeFilter, setTypeFilter] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    let typeFromUrl = searchParams.get("type");
    if (typeFromUrl && typeFromUrl !== "") {
      typeFromUrl =
        typeFromUrl.charAt(0).toUpperCase() +
        typeFromUrl.slice(1).toLowerCase();

      setTypeFilter(typeFromUrl);
    } else {
      setTypeFilter(""); // 👈 default when type is empty
    }
  }, [searchParams]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await fetchReview({
        page: currentPage,
        limit: itemsPerPage,
        search: searchQuery,
        sortBy: sortColumn,
        sortOrder: sortOrder,
        status: statusFilter,
        type: typeFilter,
        userId: userId,
      });
      
      setItems(res.data.reviews);
      setTotalItems(res.data.metadata.totalReviews);
      setTotalPages(res.data.metadata.totalPages);
    } catch (error) {
      console.log(error);
      setErrorMessage("Failed to fetch review");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [
    searchQuery,
    currentPage,
    itemsPerPage,
    sortColumn,
    sortOrder,
    statusFilter,
    typeFilter,
    userId,
  ]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };


  const getPaginationRange = () => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const maxVisiblePages = 5;
    const pages = [];
    
    if (totalPages <= maxVisiblePages) {
      // If total pages is 5 or less, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Calculate the start page for the visible range
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      
      // Adjust start page if we're near the end
      if (endPage - startPage < maxVisiblePages - 1) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  return (
    <div className="card h-100 p-0 radius-12">
      {successMessage && (
        <SuccessAlert
          message={successMessage}
          onClose={() => setSuccessMessage("")}
        />
      )}
      {errorMessage && (
        <DengerAlert
          message={errorMessage}
          onClose={() => setErrorMessage("")}
        />
      )}

      {/* Header and Filters */}
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <span className="text-md fw-medium text-secondary-light mb-0">
            Show
          </span>

          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(parseInt(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value="10">10 items per page</option>
            <option value="20">20 items per page</option>
            <option value="50">50 items per page</option>
            <option value="100">100 items per page</option>
          </select>

          <form className="navbar-search" style={{ position: "relative" }}>
            <input
              type="text"
              className="bg-base h-40-px w-auto pe-40"
              name="search"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyUp={(e) => {
                if (e.target.value === "") {
                  setCurrentPage(1);
                }
              }}
              style={{
                paddingRight: searchQuery ? "5rem" : "2.5rem",
              }}
            />
            <Icon
              icon="ion:search-outline"
              className="icon"
              style={{
                position: "absolute",
                right: searchQuery ? "3.25rem" : "1rem",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "1.25rem",
                color: "#999",
                pointerEvents: "none",
              }}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setCurrentPage(1);
                }}
                style={{
                  position: "absolute",
                  right: "1rem",
                  top: "30%",
                  transform: "translateY(-50%)",
                  fontSize: "1.25rem",
                  color: "#000",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <Icon icon="ic:round-close" />
              </button>
            )}
          </form>
        </div>
      </div>

      {/* Table */}
      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th
                  onClick={() => handleSort("userData.fullName")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    User Name
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${
                          sortColumn === "userData.fullName" &&
                          sortOrder === "asc"
                            ? "active"
                            : ""
                        }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${
                          sortColumn === "userData.fullName" &&
                          sortOrder === "desc"
                            ? "active"
                            : ""
                        }`}
                      />
                    </span>
                  </div>
                </th>
                <th
                  onClick={() => handleSort("eventData.name")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    Event Name
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${
                          sortColumn === "eventData.name" && sortOrder === "asc"
                            ? "active"
                            : ""
                        }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${
                          sortColumn === "eventData.name" &&
                          sortOrder === "desc"
                            ? "active"
                            : ""
                        }`}
                      />
                    </span>
                  </div>
                </th>
                <th
                  onClick={() => handleSort("rating")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    Rating
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${
                          sortColumn === "rating" && sortOrder === "asc"
                            ? "active"
                            : ""
                        }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${
                          sortColumn === "fullname" && sortOrder === "desc"
                            ? "active"
                            : ""
                        }`}
                      />
                    </span>
                  </div>
                </th>
                <th
                  onClick={() => handleSort("createdAt")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    Created Date
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${
                          sortColumn === "createdAt" && sortOrder === "asc"
                            ? "active"
                            : ""
                        }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${
                          sortColumn === "createdAt" && sortOrder === "desc"
                            ? "active"
                            : ""
                        }`}
                      />
                    </span>
                  </div>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center">
                    <span className="loader-15"></span>
                  </td>
                </tr>
              ) : items.length > 0 ? (
                items.map((cl) => (
                  <tr key={cl._id}>
                    <td>{cl.userData.fullName}</td>
                    <td>{cl.eventData.name}</td>
                    <td>{cl.rating}</td>
                    <td>{formatApiDate(cl.createdAt)}</td>
                    <td>
                      <div className="d-flex align-items-center gap-10">
                        <button
                          type="button"
                          onClick={() =>
                            navigate(`/review/view-review/${cl._id}`)
                          }
                          title="View"
                          className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                        >
                          <Icon
                            icon="majesticons:eye-line"
                            className="icon text-xl"
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center">
                    No Data Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
          <span>
            {totalItems === 0 ? (
              <>No entry to show</>
            ) : (
              <>
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                {totalItems} entries
              </>
            )}
          </span>

          <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                onClick={() => {
                  if (currentPage > 1) setCurrentPage((prev) => prev - 1);
                }}
              >
                <Icon icon="ep:d-arrow-left" />
              </button>
            </li>

            {getPaginationRange().map((pageNum) => (
              <li key={pageNum} className="page-item">
                <button
                  className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${
                    currentPage === pageNum
                      ? "bg-primary-600 text-white"
                      : "bg-neutral-200"
                  }`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              </li>
            ))}

            <li className="page-item">
              <button
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                disabled={currentPage === totalPages}
                onClick={() => {
                  if (currentPage < totalPages) setCurrentPage(currentPage + 1);
                }}
              >
                <Icon icon="ep:d-arrow-right" />
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ReviewListLayer;
